import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ClickProtection } from '@daas-consulting/core-frontend-utils';
import { PlanningStatus } from '../planning-statuses/enums';

@Component({
  selector: 'daas-consulting-actionable-menu',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './actionable-menu.component.html',
  styleUrl: './actionable-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionableMenuComponent implements OnDestroy {
  @Input() disabledActions = false;
  @Input() status!: PlanningStatus | string | null;
  @Input() showApprove = false;
  @Input() showProduce = false;
  @Input() showCancel = false;
  @Input() showReject = false;
  @Input() showIssue = false;
  @Input() showDeliver = false;
  @Input() showDocuments = false;

  // When true, hide all transition buttons and show only the Documents button
  @Input() onlyDocumentsMode = false;

  @Output() readonly transitionTo: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() readonly printDocuments: EventEmitter<string> =
    new EventEmitter<string>();

  readonly #clickProtection: ClickProtection<string>;

  constructor() {
    this.#clickProtection = new ClickProtection({
      context: ActionableMenuComponent.name,
    });
    this.#clickProtection.validClicks$.subscribe((value) => {
      this.transitionTo.emit(value);
    });
  }

  ngOnDestroy(): void {
    this.#clickProtection.destroy();
  }

  handleTransitionToApproved($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showApprove) {
      this._handleTransitionTo(PlanningStatus.approved);
    }
  }

  handleTransitionToRejected($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showReject) {
      this._handleTransitionTo(PlanningStatus.rejected);
    }
  }

  handleTransitionToProduced($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showProduce) {
      this._handleTransitionTo(PlanningStatus.produced);
    }
  }

  handleTransitionToIssued($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showIssue) {
      this._handleTransitionTo(PlanningStatus.issued);
    }
  }

  handleTransitionToCanceled($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showCancel && !this._isIssued()) {
      this._handleTransitionTo(PlanningStatus.canceled);
    }
  }

  handleTransitionToDelivered($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showDeliver) {
      this._handleTransitionTo(PlanningStatus.delivered);
    }
  }

  handlePrintDocuments($event: MouseEvent): void {
    $event.preventDefault();
    if (this.showDocuments) {
      this.printDocuments.emit();
    }
  }

  cancelIsDisabled(): boolean {
    const rv = !this.showCancel || this._isIssued();
    return rv;
  }

  private _handleTransitionTo(to: string): void {
    this.#clickProtection.click(to);
  }

  private _isIssued(): boolean {
    const rv = this.status === 'issued';
    return rv;
  }
}
